import type { ReactElement } from "react";
import React from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const REMOTE_URL = /https?:\/\/[^\s)>\]}]+/gi;
const MARKDOWN_IMAGE = /!\[([^\]]*)\]\([^)]*\)/g;
const MARKDOWN_LINK = /\[([^\]]+)\]\([^)]*\)/g;
const RAW_HTML = /<[^>\n]*>/g;

const soapDraftComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mb-4 mt-8 text-xl font-semibold tracking-tight text-foreground first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-3 mt-8 text-lg font-semibold tracking-tight text-foreground first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-6 text-base font-semibold text-foreground first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-2 mt-5 text-sm font-semibold text-foreground first:mt-0">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="mb-3 text-sm leading-6 text-foreground last:mb-0">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-foreground last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm text-foreground last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="pl-1 leading-6">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-2 border-primary/40 pl-4 text-sm italic text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-border" />,
  table: ({ children }) => (
    <div className="mb-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-left text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
  tr: ({ children }) => <tr className="border-b border-border last:border-b-0">{children}</tr>,
  th: ({ children }) => <th className="px-3 py-2 font-semibold text-foreground">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 align-top leading-6 text-foreground">{children}</td>,
  pre: ({ children }) => (
    <pre className="mb-4 overflow-x-auto rounded-lg bg-muted p-3 text-xs leading-5 last:mb-0">{children}</pre>
  ),
  code: ({ children }) => <code className="font-mono text-xs">{children}</code>,
  a: ({ children }) => <span>{children}</span>,
  img: () => null,
};

export function sanitizeSoapDraftMarkdown(markdown: string): string {
  return markdown
    .replace(MARKDOWN_IMAGE, "$1")
    .replace(MARKDOWN_LINK, "$1")
    .replace(RAW_HTML, "[raw HTML removed]")
    .replace(REMOTE_URL, "[external link removed]");
}

export function SoapDraftRenderer({ markdown }: { markdown: string }): ReactElement {
  return (
    <div className="max-w-none break-words text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={soapDraftComponents}
      >
        {sanitizeSoapDraftMarkdown(markdown)}
      </ReactMarkdown>
    </div>
  );
}
